import { useState } from 'react';

import { diffLines } from 'diff';
import { ChevronRight, Minus, Plus, Replace } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

import type { JudgeTuningPromptEdit } from '@/lib/api/judge-tuning.api';

// ── Edit list component (reused in iteration cards) ──────────────────────────

export const EditList = ({
  edits,
  append,
}: {
  edits: JudgeTuningPromptEdit[];
  append?: string;
}) => (
  <div className="space-y-2">
    {edits.map((edit, idx) => (
      <Card key={idx} className="border-muted shadow-none">
        <CardContent className="flex flex-col gap-1 px-3 py-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-semibold">#{idx + 1}</span>
            <OpBadge op={edit.op} />
          </div>
          {edit.find && (
            <code
              className={cn(
                'block rounded bg-rose-50 px-2 py-1 font-mono break-words whitespace-pre-wrap text-rose-700 dark:bg-rose-950 dark:text-rose-300',
                (edit.op === 'delete' || edit.op === 'replace') && 'line-through',
              )}
            >
              {edit.find}
            </code>
          )}
          {edit.replace && (
            <code className="block rounded bg-emerald-50 px-2 py-1 font-mono break-words whitespace-pre-wrap text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
              {edit.replace}
            </code>
          )}
        </CardContent>
      </Card>
    ))}
    {append && (
      <Card className="border-muted shadow-none">
        <CardContent className="flex flex-col gap-1 px-3 py-2 text-xs">
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground font-semibold">append</span>
            <Badge variant="secondary" className="text-[10px]">
              <Plus className="mr-0.5 h-3 w-3" /> append
            </Badge>
          </div>
          <code className="block rounded bg-emerald-50 px-2 py-1 font-mono break-words whitespace-pre-wrap text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300">
            {append}
          </code>
        </CardContent>
      </Card>
    )}
  </div>
);

const OP_LABEL: Record<string, { icon: typeof Plus; label: string; color: string }> = {
  replace: {
    icon: Replace,
    label: 'replace',
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  },
  insert_after: {
    icon: Plus,
    label: 'insert after',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
  insert_before: {
    icon: Plus,
    label: 'insert before',
    color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300',
  },
  delete: {
    icon: Minus,
    label: 'delete',
    color: 'bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  },
};

const OpBadge = ({ op }: { op: string }) => {
  const info = OP_LABEL[op] ?? {
    icon: Replace,
    label: op,
    color: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
  };
  const Icon = info.icon;
  return (
    <Badge variant="secondary" className={cn('text-[10px]', info.color)}>
      <Icon className="mr-0.5 h-3 w-3" /> {info.label}
    </Badge>
  );
};

// ── Collapsed unchanged lines ────────────────────────────────────────────────

const COLLAPSE_THRESHOLD = 6;

interface DiffBlock {
  kind: 'added' | 'removed' | 'unchanged';
  lines: string[];
}

/** Parse the diff result into blocks of contiguous same-kind lines. */
const buildBlocks = (before: string, after: string): DiffBlock[] => {
  const changes = diffLines(before, after);
  const blocks: DiffBlock[] = [];

  for (const change of changes) {
    const kind: DiffBlock['kind'] = change.added
      ? 'added'
      : change.removed
        ? 'removed'
        : 'unchanged';
    // diffLines includes a trailing newline on each part — strip it so the
    // rendered line count matches what the user sees.
    const raw = change.value.endsWith('\n') ? change.value.slice(0, -1) : change.value;
    const lines = raw.split('\n');
    blocks.push({ kind, lines });
  }
  return blocks;
};

// ── Shared unchanged-line renderer ───────────────────────────────────────────

const UnchangedLine = ({ line }: { line: string }) => (
  <div
    className="text-muted-foreground px-3 py-0.5 break-words whitespace-pre-wrap"
    data-testid="diff-unchanged"
  >
    <span className="mr-2 inline-block w-4 text-right opacity-30 select-none">&nbsp;</span>
    {line}
  </div>
);

// ── Main component ───────────────────────────────────────────────────────────

export interface PromptDiffProps {
  before: string;
  after: string;
  edits?: JudgeTuningPromptEdit[];
  append?: string;
}

export const PromptDiff = ({ before, after, edits, append }: PromptDiffProps) => {
  const blocks = buildBlocks(before, after);
  const hasEdits = (edits && edits.length > 0) || !!append;

  return (
    <div className="space-y-3">
      {hasEdits && <EditList edits={edits ?? []} append={append} />}
      <div
        className="overflow-x-auto rounded-lg border bg-slate-50 font-mono text-xs dark:bg-slate-900"
        data-testid="prompt-diff"
      >
        {blocks.map((block, bi) =>
          block.kind === 'unchanged' ? (
            <UnchangedBlock key={bi} lines={block.lines} />
          ) : (
            block.lines.map((line, li) => (
              <div
                key={`${bi}-${li}`}
                className={cn(
                  'px-3 py-0.5 break-words whitespace-pre-wrap',
                  block.kind === 'added' &&
                    'bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
                  block.kind === 'removed' &&
                    'bg-rose-50 text-rose-800 dark:bg-rose-950 dark:text-rose-300',
                )}
                data-testid={block.kind === 'added' ? 'diff-added' : 'diff-removed'}
              >
                <span className="mr-2 inline-block w-4 text-right opacity-50 select-none">
                  {block.kind === 'added' ? '+' : '-'}
                </span>
                {line}
              </div>
            ))
          ),
        )}
      </div>
    </div>
  );
};

/** Render an unchanged block, collapsing it when it exceeds COLLAPSE_THRESHOLD lines. */
const UnchangedBlock = ({ lines }: { lines: string[] }) => {
  const [expanded, setExpanded] = useState(false);

  if (lines.length <= COLLAPSE_THRESHOLD || expanded) {
    return (
      <>
        {lines.map((line, i) => (
          <UnchangedLine key={i} line={line} />
        ))}
      </>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className="text-muted-foreground hover:bg-muted/60 flex w-full items-center gap-1 px-3 py-1"
      data-testid="diff-collapse"
    >
      <ChevronRight className="h-3 w-3" />
      <span className="text-xs">... {lines.length} unchanged lines</span>
    </button>
  );
};
